import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Grid, FormControlLabel,
  Switch, Alert, Card, CardContent,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';

const ClinicSetupStep = ({
  formData,
  setFormData,
  onSubmit,
  submitting,
  isCompleted,
}) => {
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.clinic_address?.trim()) {
      newErrors.clinic_address = 'Clinic address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Clinic/Practice Setup
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Configure your practice location and telehealth settings
      </Typography>

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Clinic setup has been completed successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Clinic Address */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Practice Location
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Clinic Address"
                value={formData.clinic_address || ''}
                onChange={(e) => handleInputChange('clinic_address', e.target.value)}
                error={!!errors.clinic_address}
                helperText={errors.clinic_address || 'Enter your complete clinic address including city, state, and postal code'}
                placeholder="Enter your complete clinic address..."
                disabled={isCompleted}
              />
              
              <Typography variant="body2" color="text.secondary" mt={1}>
                This address will be visible to patients when they book appointments
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Telehealth Settings */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Telehealth Settings
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.telehealth_enabled || false}
                    onChange={(e) => handleInputChange('telehealth_enabled', e.target.checked)}
                    disabled={isCompleted}
                  />
                }
                label="Enable Telehealth Consultations"
              />
              
              <Typography variant="body2" color="text.secondary" mt={1}>
                When enabled, patients can book video consultations with you. 
                You'll receive video call links and can conduct consultations remotely.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Benefits of enabling telehealth:</strong>
              </Typography>
              
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Reach patients across different locations
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Flexible consultation scheduling
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Reduced travel time for patients
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Secure video consultation platform
                </Typography>
              </Box>
              
              <Alert severity="info">
                You can always change these settings later from your dashboard
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {!isCompleted && (
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ClinicSetupStep;
